import { useState, useEffect } from 'react';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingScreen';
import { AppIcon } from '@/components/AppIcon';
import { CategoryMark } from '@/components/CategoryMark';

interface Budget {
  id: string;
  categoryId: string;
  categoryName: string;
  categoryIcon: string;
  categoryColor: string;
  amount: number;
  spent: number;
  remaining: number;
  percentage: number;
}

export default function BudgetsPage() {
  const { isLoading: isAuthLoading } = useAuthStore();
  const { currentYearMonth, setCurrentYearMonth, showToast } = useAppStore();
  const [isLoading, setIsLoading] = useState(true);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [totalBudget, setTotalBudget] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    // 認証が完了してからAPIを呼び出す
    if (!isAuthLoading) {
      loadBudgets();
    }
  }, [currentYearMonth, isAuthLoading]);

  const loadBudgets = async () => {
    setIsLoading(true);
    try {
      // サマリーを取得（支出情報含む）
      const summaryRes = await api.getBudgetSummary(currentYearMonth);
      console.log('Budget summary response:', summaryRes);
      const summaryData = summaryRes.summary || [];
      console.log('Summary data:', summaryData);

      // Budget型にマッピング
      const budgetData: Budget[] = summaryData.map((item: any) => ({
        id: item.categoryId,
        categoryId: item.categoryId,
        categoryName: item.categoryName,
        categoryIcon: item.categoryIcon,
        categoryColor: item.categoryColor,
        amount: item.budget,
        spent: item.spent,
        remaining: item.remaining,
        percentage: item.percentage,
      }));

      console.log('Budget data to set:', budgetData);
      setBudgets(budgetData);
      setTotalBudget(summaryData.reduce((sum: number, item: any) => sum + (item.budget || 0), 0));
      setTotalSpent(summaryData.reduce((sum: number, item: any) => sum + (item.spent || 0), 0));
    } catch (error) {
      console.error('Failed to load budgets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveBudget = async (categoryId: string) => {
    const amount = parseInt(editValue) || 0;
    try {
      await api.upsertBudget({
        categoryId,
        yearMonth: currentYearMonth,
        amount,
      });
      setEditingId(null);
      loadBudgets();
      showToast('予算を更新しました', 'success');
    } catch (error) {
      showToast('予算の更新に失敗しました', 'error');
    }
  };

  const changeMonth = (delta: number) => {
    const [year, month] = currentYearMonth.split('-').map(Number);
    const newDate = new Date(year, month - 1 + delta, 1);
    const newYearMonth = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`;
    setCurrentYearMonth(newYearMonth);
  };

  const formatYearMonth = (yearMonth: string) => {
    const [year, month] = yearMonth.split('-');
    return `${year}年${parseInt(month)}月`;
  };

  const formatCurrency = (amount: number) => {
    return `¥${amount.toLocaleString()}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="pb-4">
      {/* ヘッダー */}
      <div className="page-topbar">
        <div className="page-topbar-inner">
          <button
            onClick={() => changeMonth(-1)}
            className="icon-button"
            aria-label="前の月"
          >
            ←
          </button>
          <h1 className="text-lg font-bold">{formatYearMonth(currentYearMonth)}</h1>
          <button
            onClick={() => changeMonth(1)}
            className="icon-button"
            aria-label="次の月"
          >
            →
          </button>
        </div>
      </div>

      {/* 総合サマリー */}
      <div className="p-4">
        <div className="card p-4">
          <h2 className="text-sm font-medium text-gray-500 mb-3">予算サマリー</h2>
          <div className="flex items-end gap-2 mb-3">
            <span className="text-3xl font-bold">{formatCurrency(totalSpent)}</span>
            <span className="text-gray-500 mb-1">/ {formatCurrency(totalBudget)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div
              className={`h-2 rounded-full transition-all ${totalSpent > totalBudget
                ? 'bg-red-500'
                : totalSpent > totalBudget * 0.8
                  ? 'bg-yellow-500'
                  : 'bg-primary-500'
                }`}
              style={{
                width: `${Math.min((totalSpent / (totalBudget || 1)) * 100, 100)}%`,
              }}
            />
          </div>
          <p className="text-sm text-gray-600">
            残り: {formatCurrency(totalBudget - totalSpent)}
          </p>
        </div>
      </div>

      {/* カテゴリ別予算 */}
      <div className="px-4">
        <h2 className="font-medium mb-3">カテゴリ別予算</h2>
        <div className="space-y-3">
          {budgets.map((budget) => {
            const isEditing = editingId === budget.categoryId;

            return (
              <div key={budget.categoryId} className="card p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CategoryMark name={budget.categoryName} color={budget.categoryColor} size="sm" />
                    <span className="font-medium">{budget.categoryName}</span>
                  </div>
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-24 px-2 py-1 border rounded text-right"
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveBudget(budget.categoryId)}
                        className="text-primary-500 text-sm font-semibold"
                      >
                        保存
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="text-gray-500 text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setEditingId(budget.categoryId);
                        setEditValue(String(budget.amount));
                      }}
                      className="text-sm text-gray-500"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {formatCurrency(budget.amount)} <AppIcon name="edit" size={14} />
                      </span>
                    </button>
                  )}
                </div>

                <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                  <div
                    className="h-1.5 rounded-full transition-all"
                    style={{
                      width: `${Math.min((budget.spent / (budget.amount || 1)) * 100, 100)}%`,
                      backgroundColor:
                        budget.spent > budget.amount
                          ? '#ef4444'
                          : budget.spent > budget.amount * 0.8
                            ? '#eab308'
                            : budget.categoryColor,
                    }}
                  />
                </div>

                <div className="flex justify-between text-sm text-gray-500">
                  <span>使用: {formatCurrency(budget.spent)}</span>
                  <span>残り: {formatCurrency(budget.amount - budget.spent)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
